import { useEffect } from 'react';

/** CSS custom property that carries the measured scrollbar width. */
const SCROLLBAR_WIDTH_VAR = '--ws-nav-drawer-scrollbar-width';

/** Class Bootstrap uses on <body> to compensate for the removed scrollbar. */
const MODAL_OPEN_CLASS = 'modal-open';

/**
 * Measures the browser's native scrollbar width using an offscreen probe
 * element, instead of the simpler
 * `window.innerWidth - document.documentElement.clientWidth`.
 *
 * Here's a concrete sequence where that simpler subtraction gives the wrong
 * answer every time, not just occasionally:
 *   1. The page loads with a visible scrollbar. innerWidth includes it;
 *      clientWidth doesn't. Subtracting them would correctly give the
 *      scrollbar's width — if we measured right now.
 *   2. The user opens My Cars without the drawer already open. ws-mycars-nav's
 *      own modal hides the page's scrollbar as part of opening — and it does
 *      this earlier than this hook can react to it (US2059853 T54/T55).
 *   3. By the time this hook's code actually runs, the scrollbar is already
 *      gone. innerWidth and clientWidth are now equal, so the subtraction
 *      reads 0 — every time this happens, not as an occasional glitch.
 *
 * An offscreen probe sidesteps this: instead of reading the page's real
 * scrollbar (which might already be hidden by the time we check), it makes
 * its own disposable scrollbar on a hidden element and measures that
 * directly (the same technique `dom-helpers/util/scrollbarSize` uses, which
 * react-bootstrap itself already depends on). Its answer doesn't depend on
 * whether some other widget already hid the page's real one.
 *
 * @returns {number}
 */
const measureScrollbarWidth = () => {
	const probe = document.createElement('div');
	probe.style.cssText =
		'position:absolute;top:-9999px;width:50px;height:50px;overflow:scroll;';
	document.body.appendChild(probe);
	const width = probe.offsetWidth - probe.clientWidth;
	document.body.removeChild(probe);
	return width;
};

/**
 * Locks page scroll while `isLocked` is true.
 *
 * Measures the scrollbar width lost when the page can no longer scroll and
 * exposes it via a CSS custom property on <html>, then adds `modal-open` to
 * <body> so the existing padding-compensation styles keyed off that class kick
 * in. Both are cleared when `isLocked` becomes false and on unmount.
 *
 * Idempotent alongside Bootstrap's own `modal-open` toggling: this repo only
 * ever has one of {drawer, mega menu} open at a time, so a plain boolean toggle
 * is safe.
 *
 * @param {boolean} isLocked - Whether page scroll should currently be locked.
 */
const useBodyScrollLock = (isLocked) => {
	useEffect(() => {
		if (!isLocked || typeof window === 'undefined') return undefined;

		const scrollbarWidth = measureScrollbarWidth();
		document.documentElement.style.setProperty(
			SCROLLBAR_WIDTH_VAR,
			`${scrollbarWidth}px`
		);
		document.body.classList.add(MODAL_OPEN_CLASS);

		return () => {
			document.documentElement.style.removeProperty(SCROLLBAR_WIDTH_VAR);
			document.body.classList.remove(MODAL_OPEN_CLASS);
		};
	}, [isLocked]);
};

export default useBodyScrollLock;
